"use client";
import type { InputChipProps } from "@components/input-chip";
import type { MenuContentProps } from "@components/menu-content";
import type { TextFieldProps } from "@components/text-field";
import type { IconProps } from "@icons/utils/interface";
import type { CommonProps } from "../../types/common-props";

export const selectValues = {
  sizes: {
    sm: "sm",
    md: "md",
    lg: "lg",
  } as const,
  validations: {
    error: "error",
    success: "success",
  } as const,
  types: {
    text: "text",
    chip: "chip",
  } as const,
};

export const selectDefaults = {
  size: selectValues.sizes.md,
  type: selectValues.types.text,
};

export interface SelectOption {
  /** Whether the option is disabled and cannot be selected */
  disabled?: boolean;
  /** The text label displayed for the option */
  label: string;
  /** Optional icon component displayed at the start of the option */
  StartIcon?: React.FC<IconProps>;
  /** The value for this option */
  value: string;
}

export interface SelectOwnProps {
  /** The preferred alignment of the select content against the trigger. */
  align?: MenuContentProps["align"];
  /** If true, the component is disabled. */
  disabled?: boolean;
  /** The helper text of the component. */
  helperText?: string;
  /** Reserve one line of text for validation feedback.  */
  helperTextReserveSpace?: boolean;
  /** The id of the select input. */
  id?: string;
  /** The label of the component. */
  label?: string;
  /** The secondary label of the component. */
  labelSecondary?: string;
  /** The name of the select input. */
  name?: string;
  /** If true, the select is open. */
  open?: boolean;
  /** The options of the select. */
  options: SelectOption[];
  /** The placeholder text. */
  placeholder?: string;
  /** If true, the select is required. */
  required?: boolean;
  /** Where the select content resides in relation to the trigger. */
  side?: MenuContentProps["side"];
  /** The size of the select. */
  size?: (typeof selectValues.sizes)[keyof typeof selectValues.sizes];
  /** The icon or text to display at the start of the select. */
  startSlot?: TextFieldProps["startSlot"];
  /** The icon of the tooltip. The icon is given a translated "Info" as its accessible name; to override that, pass a
   * wrapper that re-applies `titleAccess` after spreading its props:
   * `const Warn = (props) => <SomeIcon {...props} titleAccess="Warning" />`. */
  TooltipIcon?: React.FC<IconProps>;
  /** The tooltip of the component. */
  tooltipText?: string;
  /** The validation state of the component. */
  validation?: (typeof selectValues.validations)[keyof typeof selectValues.validations];
}

export type SelectBaseProps = Omit<CommonProps<"div">, "size"> & SelectOwnProps;

export interface SelectMultipleChipProps extends SelectBaseProps {
  /** Props for InputChip when type is "chip". Only applies to multiple select. */
  chipProps?: InputChipProps;
  /** The default value. Use when the component is not controlled. */
  defaultValue?: string[];
  /** Maximum number of chips to display. When exceeded, shows "+N" indicator. Only applies when type is "chip" with multiple select. */
  limitChips?: number;
  /** If true, allows multiple selections. */
  multiple: true;
  /** Callback fired when the dropdown closes. Use for operations like data fetching that should only happen once after all selections are made. */
  onClose?: (value: string[]) => void;
  /** Callback fired when the value changes. Use for immediate UI updates that don't require page refresh. */
  onValueChange?: (value: string[]) => void;
  /** The display type of selected values. Note: "chip" type only works with multiple selection. */
  type: "chip";
  /** The current value. Use when the component is controlled. */
  value?: string[];
}

export interface SelectMultipleTextProps extends SelectBaseProps {
  /** The default value. Use when the component is not controlled. */
  defaultValue?: string[];
  /** If true, allows multiple selections. */
  multiple: true;
  /** Callback fired when the dropdown closes. Use for operations like data fetching that should only happen once after all selections are made. */
  onClose?: (value: string[]) => void;
  /** Callback fired when the value changes. Use for immediate UI updates that don't require page refresh. */
  onValueChange?: (value: string[]) => void;
  /** The display type of selected values. */
  type?: "text";
  /** The current value. Use when the component is controlled. */
  value?: string[];
}

export interface SelectSingleTextProps extends SelectBaseProps {
  /** The default value. Use when the component is not controlled. */
  defaultValue?: string;
  /** If true, allows multiple selections. */
  multiple?: false;
  /** Callback fired when the value changes. Use for immediate UI updates that don't require page refresh. */
  onValueChange?: (value: string) => void;
  /** The display type of selected values. */
  type?: "text";
  /** The current value. Use when the component is controlled. */
  value?: string;
}

export type SelectProps = SelectMultipleChipProps | SelectMultipleTextProps | SelectSingleTextProps;
